import {isAppleRecurrence} from '../../../../supabase/functions/_shared/appleCalendarWriteDomain';
import {NativeAppleCalendarActionModal,type AppleCalendarActionPrefill} from '@/components/mirror/NativeAppleCalendarActionModal';
import { CalendarCreationDestinationModal } from "@/components/mirror/CalendarCreationDestinationModal";
import type { CalendarActionPrefill } from "@/lib/calendarAction";
import { localDraftForCalendarCreate } from "@/lib/calendarCreationDestination";
import { agendaCaptureHandler } from "@/lib/capture/handlers/agendaCaptureHandler";
import { agendaOriginOperationId } from "@/lib/agendaExecutor";
import { agendaReminderOffsets } from "@/lib/agendaCaptureData";
import { EVENT_DURATION_MIN_BY_TAG } from "@/lib/agendaDurations";
import { deviceTimeZone } from "@/lib/reminderSchedule";
import type { ModuleCaptureHandler } from "@/lib/capture/types";

const text = (value: unknown, max: number) => typeof value === "string" ? value.trim().slice(0, max) : "";

export const calendarCaptureHandler: ModuleCaptureHandler<CalendarActionPrefill|AppleCalendarActionPrefill> = {
  tool: "propose_calendar_action",
  getRawText: (proposal) => {
    if (proposal.tool !== "propose_calendar_action") return "";
    const args = proposal.args as unknown as Record<string, unknown>;
    return JSON.stringify({ action: text(args.action, 20), provider: text(args.provider, 30),
      query: text(args.query, 500), event_id: text(args.event_id, 1500), title: text(args.title, 500),
      start: text(args.start, 80), end: text(args.end, 80), all_day: args.all_day === true,
      time_zone: text(args.time_zone, 100), location: text(args.location, 1000), description: text(args.description, 10_000),
      native_recurrence:args.native_recurrence, raw_text: typeof args.raw_text === "string" ? args.raw_text : undefined });
  },
  extract: async (_raw, proposal, ctx) => {
    if (proposal.tool !== "propose_calendar_action" || !ctx.ownerId || !ctx.sessionId) return null;
    const args = proposal.args as unknown as Record<string, unknown>;
    if (!["search", "create", "update", "delete"].includes(String(args.action))) return null;
    if (!["auto", "google_calendar", "outlook", "apple_calendar"].includes(String(args.provider ?? "auto"))) return null;
    if(args.provider==='apple_calendar'){
      const native:AppleCalendarActionPrefill={provider:'apple_calendar',action:args.action as AppleCalendarActionPrefill['action'],query:text(args.query,500),action_id:proposal.action_id,
        expected_user_id:ctx.ownerId,expected_session_id:ctx.sessionId,
        title:typeof args.title==='string'?text(args.title,500):undefined,start:typeof args.start==='string'?text(args.start,80):undefined,end:typeof args.end==='string'?text(args.end,80):undefined,
        all_day:typeof args.all_day==='boolean'?args.all_day:undefined,time_zone:text(args.time_zone,100)||undefined,location:typeof args.location==='string'?text(args.location,1000):undefined,
        description:typeof args.description==='string'?text(args.description,10000):undefined,...(isAppleRecurrence(args.native_recurrence)?{recurrence:args.native_recurrence}:{})};
      if(args.native_recurrence!==undefined&&(!isAppleRecurrence(args.native_recurrence)||native.action!=='create'))return null;
      // Apple locators come only from fresh device selection, never a model ID.
      if(native.action==='create'&&typeof args.raw_text==='string'){
        if(!args.raw_text.trim()||args.raw_text.length>20000)return null;
        const draft=await agendaCaptureHandler.extract(args.raw_text,{tool:'propose_agenda_event',action_id:proposal.action_id,preview:proposal.preview,args:{raw_text:args.raw_text}},ctx);ctx.assertCurrent?.();
        if(!draft?.title||!draft.start_at)return null;
        Object.assign(native,{title:draft.title,start:draft.start_at,end:draft.end_at??new Date(Date.parse(draft.start_at)+EVENT_DURATION_MIN_BY_TAG[draft.tag??'Focus']*60000).toISOString(),
          all_day:false,time_zone:deviceTimeZone(),location:draft.location??undefined,description:draft.notes??undefined});
      }
      return native;
    }
    const prefill: CalendarActionPrefill = {
      action: args.action as CalendarActionPrefill["action"],
      provider: (args.provider ?? "auto") as CalendarActionPrefill["provider"],
      query: text(args.query, 500), event_id: text(args.event_id, 1500) || undefined,
      title: typeof args.title === "string" ? text(args.title, 500) : undefined,
      start: typeof args.start === "string" ? text(args.start, 80) : undefined,
      end: typeof args.end === "string" ? text(args.end, 80) : undefined,
      all_day: typeof args.all_day === "boolean" ? args.all_day : undefined,
      time_zone: text(args.time_zone, 100) || undefined,
      location: typeof args.location === "string" ? text(args.location, 1000) : undefined,
      description: typeof args.description === "string" ? text(args.description, 10_000) : undefined,
      action_id: proposal.action_id, expected_user_id: ctx.ownerId, expected_session_id: ctx.sessionId,
    };
    if (prefill.action === "create" && typeof args.raw_text === "string") {
      if (!args.raw_text.trim() || args.raw_text.length > 20_000) return null;
      const draft = await agendaCaptureHandler.extract(args.raw_text, { tool: "propose_agenda_event", action_id: proposal.action_id,
        preview: proposal.preview, args: { raw_text: args.raw_text } }, ctx);
      ctx.assertCurrent?.();
      if (!draft || !draft.title || !draft.start_at || !Number.isFinite(Date.parse(draft.start_at))) return null;
      const tag = draft.tag ?? "Focus";
      // Use the same visible, editable duration default as the local create form.
      const end = draft.end_at ?? new Date(Date.parse(draft.start_at) + EVENT_DURATION_MIN_BY_TAG[tag] * 60_000).toISOString();
      if (!Number.isFinite(Date.parse(end)) || Date.parse(end) <= Date.parse(draft.start_at)) return null;
      prefill.title = draft.title; prefill.start = draft.start_at; prefill.end = end; prefill.all_day = false;
      prefill.time_zone = deviceTimeZone(); prefill.location = draft.location ?? undefined; prefill.description = draft.notes ?? undefined;
      prefill.vyv_draft = await localDraftForCalendarCreate(prefill, ctx.assertCurrent);
      if (!prefill.vyv_draft) return null;
      const offsets = agendaReminderOffsets(draft.reminders, draft.reminder_minutes);
      prefill.vyv_draft.reminders = await Promise.all(offsets.map(async (reminder_minutes, index) => ({
        id: await agendaOriginOperationId(ctx.ownerId!, ctx.sessionId!, proposal.action_id, "reminder:" + index, ctx.assertCurrent), reminder_minutes,
      })));
      ctx.assertCurrent?.();
      Object.assign(prefill.vyv_draft, { tag, source: "mirror", transcript_raw: draft.transcript_raw,
        confidence: draft.confidence, notes: draft.notes ?? null, location: draft.location ?? null });
      return prefill;
    }
    prefill.vyv_draft = await localDraftForCalendarCreate(prefill, ctx.assertCurrent);
    ctx.assertCurrent?.();
    return prefill;
  },
  renderModal: ({ open, prefill, onClose, onCreated }) => (
    prefill?.provider==='apple_calendar'?<NativeAppleCalendarActionModal open={open} prefill={prefill} onClose={onClose} onCreated={onCreated}/>:<CalendarCreationDestinationModal open={open} prefill={prefill} onClose={onClose} onCreated={onCreated} />
  ),
};
